import { useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import {
  getClassificationScatterPageDomain,
  type ClassificationPageMetric,
} from '../lib/classificationVisuals';
import { ScatterPagePreviewTooltip } from './ScatterPagePreviewTooltip';
import { marginToColor } from '../lib/classificationMarginColor';

export function ClassificationConfidenceScatter({
  pages,
  pageCount,
  onPageClick,
}: {
  pages: ClassificationPageMetric[];
  pageCount: number;
  onPageClick?: (pageNumber: number) => void;
}) {
  const chartData = useMemo(
    () =>
      pages.map((p) => ({
        pageId: p.pageId,
        pageNumber: p.pageNumber,
        confidence: +(p.confidence * 100).toFixed(1),
        margin: p.margin,
        marginPp: +(p.margin * 100).toFixed(1),
        top1Category: p.top1Category,
      })),
    [pages],
  );

  if (!pages.length) return null;

  return (
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart accessibilityLayer={false} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
          <XAxis
            dataKey="pageNumber"
            type="number"
            domain={getClassificationScatterPageDomain(pageCount)}
            tick={{ fontSize: 11 }}
            label={{ value: 'Page', position: 'insideBottom', offset: -2, fontSize: 10 }}
          />
          <YAxis
            dataKey="confidence"
            type="number"
            domain={[0, 100]}
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => `${v}%`}
            label={{ value: 'Confidence', angle: -90, position: 'insideLeft', fontSize: 10 }}
          />
          <ZAxis range={[40, 40]} />
          <ReferenceLine
            y={85}
            stroke="#6366f1"
            strokeDasharray="4 4"
            label={{ value: '85%', position: 'right', fontSize: 10, fill: '#6366f1' }}
          />
          <Tooltip
            content={
              <ScatterPagePreviewTooltip
                pageIdKey="pageId"
                stats={[
                  { key: 'top1Category', label: 'Classification', bold: true, optional: true },
                  { key: 'confidence', label: 'Confidence', format: (v) => `${v}%` },
                  { key: 'marginPp', label: 'Margin', format: (v) => `${v}pp` },
                ]}
              />
            }
          />
          <Scatter data={chartData} fillOpacity={0.7} cursor={onPageClick ? 'pointer' : undefined}>
            {chartData.map((entry) => (
              <Cell
                key={entry.pageNumber}
                fill={marginToColor(entry.margin)}
                onClick={() => onPageClick?.(entry.pageNumber)}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
